import React, { useState } from 'react';
import { Calculator, Copy, Trash2, TrendingUp, Info, Target, Lightbulb } from 'lucide-react';

const ConversionRateCalculatorTool = () => {
  const [visitors, setVisitors] = useState('');
  const [conversions, setConversions] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState('');

  const calculate = () => {
    const v = parseFloat(visitors);
    const c = parseFloat(conversions);

    if (isNaN(v) || isNaN(c) || v <= 0) {
      setResult(null);
      return;
    }

    // Conversion Rate = (Conversions / Visitors) × 100
    const conversionRate = (c / v) * 100;
    const nonConverted = v - c;
    const nonConvertedRate = 100 - conversionRate;

    // Determine performance level
    let performance = 'low';
    let performanceColor = 'text-red-400';
    if (conversionRate >= 5) {
      performance = 'excellent';
      performanceColor = 'text-green-400';
    } else if (conversionRate >= 3) {
      performance = 'good';
      performanceColor = 'text-blue-400';
    } else if (conversionRate >= 1) {
      performance = 'average';
      performanceColor = 'text-yellow-400';
    }

    setResult({
      visitors: v,
      conversions: c,
      conversionRate,
      nonConverted,
      nonConvertedRate,
      performance,
      performanceColor
    });
  };

  const copyResult = (value, label) => {
    navigator.clipboard.writeText(value.toFixed(2));
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const clearAll = () => {
    setVisitors('');
    setConversions('');
    setResult(null);
  };

  const formatNumber = (value) => {
    return value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  const benchmarks = [
    { industry: 'E-commerce (Overall)', rate: '1.5% - 3%', color: 'text-blue-400' },
    { industry: 'E-commerce (Top 10%)', rate: '5% - 11%', color: 'text-green-400' },
    { industry: 'SaaS Free Trial', rate: '3% - 8%', color: 'text-purple-400' },
    { industry: 'SaaS Paid Signup', rate: '2% - 5%', color: 'text-purple-400' },
    { industry: 'B2B Lead Gen', rate: '2% - 5%', color: 'text-orange-400' },
    { industry: 'B2C Lead Gen', rate: '5% - 15%', color: 'text-orange-400' },
    { industry: 'Landing Pages', rate: '2% - 5%', color: 'text-cyan-400' },
    { industry: 'Email Signup', rate: '1% - 5%', color: 'text-pink-400' },
  ];

  return (
    <div className="space-y-6">
      {/* Input Section */}
      <div className="p-4 bg-gray-800 rounded-lg space-y-4">
        <div className="flex items-center gap-2 mb-2">
          <Target size={18} className="text-blue-400" />
          <h3 className="text-sm font-medium text-gray-300">Traffic Data</h3>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Total Visitors
            </label>
            <input
              type="number"
              value={visitors}
              onChange={(e) => setVisitors(e.target.value)}
              placeholder="e.g., 10000"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="visitors-input"
            />
            <p className="text-xs text-gray-500 mt-1">Total number of visitors/sessions</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Conversions
            </label>
            <input
              type="number"
              value={conversions}
              onChange={(e) => setConversions(e.target.value)}
              placeholder="e.g., 250"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white placeholder-gray-500 focus:border-blue-500 focus:outline-none"
              data-testid="conversions-input"
            />
            <p className="text-xs text-gray-500 mt-1">Completed goal actions (purchases, signups, etc.)</p>
          </div>
        </div>
      </div>

      {/* Calculate Button */}
      <button
        onClick={calculate}
        className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
        data-testid="calculate-conversion-btn"
      >
        <Calculator size={18} />
        Calculate Conversion Rate
      </button>

      {/* Results Section */}
      {result && (
        <div className="space-y-4" data-testid="conversion-results">
          <div className="p-4 bg-gray-800 rounded-lg space-y-4">
            {/* Main Result */}
            <div className="p-4 bg-gray-900 rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <TrendingUp size={18} className="text-green-400" />
                  <span className="text-sm text-gray-400">Conversion Rate</span>
                </div>
                <button
                  onClick={() => copyResult(result.conversionRate, 'rate')}
                  className={`p-1.5 rounded transition-colors ${
                    copied === 'rate' ? 'bg-green-600' : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                  data-testid="copy-conversion-rate"
                >
                  <Copy size={14} />
                </button>
              </div>
              <div className="text-4xl font-bold text-green-400" data-testid="conversion-rate-result">
                {result.conversionRate.toFixed(2)}%
              </div>
              <div className={`text-sm mt-1 ${result.performanceColor}`}>
                Performance: {result.performance.charAt(0).toUpperCase() + result.performance.slice(1)}
              </div>
            </div>

            {/* Visual Breakdown */}
            <div className="p-4 bg-gray-900 rounded-lg">
              <div className="text-sm font-medium text-gray-300 mb-3">Conversion Funnel</div>
              <div className="relative h-8 bg-gray-700 rounded-lg overflow-hidden">
                <div 
                  className="absolute left-0 top-0 h-full bg-green-500 rounded-l-lg transition-all"
                  style={{ width: `${Math.min(result.conversionRate, 100)}%` }}
                />
              </div>
              <div className="flex justify-between mt-2 text-xs text-gray-400">
                <span className="text-green-400">Converted: {formatNumber(result.conversions)} ({result.conversionRate.toFixed(1)}%)</span>
                <span>Not Converted: {formatNumber(result.nonConverted)} ({result.nonConvertedRate.toFixed(1)}%)</span>
              </div>
            </div>

            {/* Summary */}
            <div className="border-t border-gray-700 pt-4">
              <h4 className="text-sm font-medium text-gray-300 mb-3">Summary</h4>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Total Visitors</span>
                  <span className="text-white font-medium">{formatNumber(result.visitors)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Conversions</span>
                  <span className="text-green-400 font-medium">{formatNumber(result.conversions)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Not Converted</span>
                  <span className="text-gray-400 font-medium">{formatNumber(result.nonConverted)}</span>
                </div>
                <div className="flex justify-between text-sm border-t border-gray-700 pt-2 mt-2">
                  <span className="text-gray-400">Conversion Rate</span>
                  <span className="text-green-400 font-medium">{result.conversionRate.toFixed(2)}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* What is Conversion Rate */}
      <div className="p-4 bg-gray-800 rounded-lg">
        <div className="flex items-center gap-2 mb-3">
          <Info size={18} className="text-blue-400" />
          <h3 className="text-sm font-medium text-gray-300">What is Conversion Rate?</h3>
        </div>
        <div className="space-y-3 text-sm text-gray-400">
          <p>
            <span className="text-white font-medium">Conversion rate</span> measures the percentage of visitors 
            who complete a desired action (conversion) out of the total number of visitors. It's one of the most 
            important metrics in digital marketing.
          </p>
          <div className="p-3 bg-gray-900 rounded">
            <div className="text-xs text-gray-500 mb-2">Formula:</div>
            <div className="font-mono text-yellow-400">
              Conversion Rate = (Conversions ÷ Total Visitors) × 100
            </div>
          </div>
          <p>
            <span className="text-white font-medium">What counts as a conversion?</span> It depends on your goal:
          </p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>E-commerce: Purchase completion</li>
            <li>SaaS: Free trial signup or paid subscription</li>
            <li>Lead Gen: Form submission or demo request</li>
            <li>Content: Newsletter signup or download</li>
            <li>Engagement: Account creation or app install</li>
          </ul>
        </div>
      </div>

      {/* Industry Benchmarks */}
      <div className="p-4 bg-gray-800 rounded-lg">
        <div className="flex items-center gap-2 mb-4">
          <Target size={18} className="text-purple-400" />
          <h3 className="text-sm font-medium text-gray-300">Industry Benchmarks</h3>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          {benchmarks.map((item, index) => (
            <div key={index} className="flex justify-between items-center p-2 bg-gray-900 rounded">
              <span className="text-sm text-gray-400">{item.industry}</span>
              <span className={`text-sm font-medium ${item.color}`}>{item.rate}</span>
            </div>
          ))}
        </div>
        <p className="text-xs text-gray-500 mt-3">
          Note: Benchmarks vary widely by industry, traffic source, and product. Use these as general guidelines, 
          not absolute targets. Focus on improving your own baseline.
        </p>
      </div>

      {/* Tips to Improve */}
      <div className="p-4 bg-gray-800 rounded-lg">
        <div className="flex items-center gap-2 mb-4">
          <Lightbulb size={18} className="text-yellow-400" />
          <h3 className="text-sm font-medium text-gray-300">Tips to Improve Conversion Rate</h3>
        </div>
        <div className="space-y-3">
          <div className="p-3 bg-gray-900 rounded-lg">
            <h4 className="text-sm font-medium text-white mb-1">1. Optimize Your Value Proposition</h4>
            <p className="text-xs text-gray-400">
              Make your unique value clear within seconds. Visitors should immediately understand why they should convert.
            </p>
          </div>
          <div className="p-3 bg-gray-900 rounded-lg">
            <h4 className="text-sm font-medium text-white mb-1">2. Reduce Friction</h4>
            <p className="text-xs text-gray-400">
              Simplify forms, reduce steps, offer guest checkout, and eliminate unnecessary fields. Every extra step loses visitors.
            </p>
          </div>
          <div className="p-3 bg-gray-900 rounded-lg">
            <h4 className="text-sm font-medium text-white mb-1">3. Add Social Proof</h4>
            <p className="text-xs text-gray-400">
              Display testimonials, reviews, case studies, trust badges, and customer logos. People follow what others do.
            </p>
          </div>
          <div className="p-3 bg-gray-900 rounded-lg">
            <h4 className="text-sm font-medium text-white mb-1">4. A/B Test Everything</h4>
            <p className="text-xs text-gray-400">
              Test headlines, CTAs, colors, images, and layouts. Small changes can lead to significant improvements.
            </p>
          </div>
          <div className="p-3 bg-gray-900 rounded-lg">
            <h4 className="text-sm font-medium text-white mb-1">5. Improve Page Speed</h4>
            <p className="text-xs text-gray-400">
              Each second of delay reduces conversions by ~7%. Optimize images, use CDN, and minimize scripts.
            </p>
          </div>
          <div className="p-3 bg-gray-900 rounded-lg">
            <h4 className="text-sm font-medium text-white mb-1">6. Create Urgency</h4>
            <p className="text-xs text-gray-400">
              Use limited-time offers, countdown timers, and scarcity indicators (but keep them genuine).
            </p>
          </div>
        </div>
      </div>

      {/* Clear Button */}
      <button
        onClick={clearAll}
        className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
        data-testid="clear-conversion-btn"
      >
        <Trash2 size={16} />
        Clear All
      </button>
    </div>
  );
};

export default ConversionRateCalculatorTool;
